/* @flow */
import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';
import { GLYPHS } from 'constants/MediumConstants';
import i18n from '@remind/i18n';
import jstz from 'jstimezonedetect';

export default class Device extends Entity {
  isEnabled() {
    return this.get('enabled');
  }

  isVerified() {
    return this.get('verified');
  }

  isDeliverable() {
    return this.get('deliverable');
  }

  setEnabled(value: boolean) {
    return this.set('enabled', value);
  }

  getLabel() {
    return this.get('label') || this.getDefaultLabel();
  }

  getEndpoint() {
    return this.get('endpoint');
  }

  getDefaultLabel() {
    switch (this.getType()) {
      case 'email': return i18n.getText('Email device');
      case 'apns': return i18n.getText('Apple device');
      case 'sms': return i18n.getText('Sms device');
      case 'gcm': return i18n.getText('Android device');
      case 'adm': return i18n.getText('Amazon device');
      default: return 'Device';
    }
  }

  getType() {
    return this.get('type');
  }

  getNotificationType() {
    switch (this.getType()) {
      case 'email': return 'email notifications';
      case 'gcm': return 'push notifications';
      case 'apns': return 'push notifications';
      case 'sms': return 'text notifications';
      default: return 'notifications';
    }
  }

  getMessageType() {
    switch (this.getType()) {
      case 'email': return 'emails';
      case 'gcm': return 'push notifications';
      case 'apns': return 'push notifications';
      case 'sms': return 'text messages';
      default: return 'notifications';
    }
  }

  getGlyphName() {
    return GLYPHS[this.getType()];
  }

  cannotBeEnabled() {
    return (this.getType() === 'email' && !this.isDeliverable());
  }

  enable() {
    return apiRequest(`/devices/${this.id}/enable`, 'POST').then(device => this.set(device));
  }

  disable() {
    return apiRequest(`/devices/${this.id}/disable`, 'POST').then(device => this.set(device));
  }

  delete() {
    return apiRequest(`/devices/${this.id}`, 'DELETE');
  }

  resendConfirmationEmail() {
    return apiRequest('/devices/email/resend', 'POST', { uid: this.getEndpoint() });
  }

  static confirmEmailDevice(token) {
    return apiRequest('/devices/email/confirm', 'POST', {
      verification_token: token,
    });
  }

  static enableEmailDevice() {
    return apiRequest('/devices/email/enable', 'POST').then(device => Device.fromJSON(device));
  }

  static createEmailDevice(uid) {
    return apiRequest('/devices/email', 'POST', {
      uid,
    }).then(device => Device.fromJSON(device));
  }

  /* you give this method 'sms://+4156152427' or 'email://q@q.com' and the
   * promise will be resolved if the user owning +4156152427 can
   * sign into the web (has a password and email), rejected otherwise.
   *
   * 200 Success. this endpoint belongs to a full account user
   * 403 The address is invalid
   * 404 The address is uknown or belong to a not full account user.
   */
  static belongsToFullAccount(address) {
    return apiRequest('/devices/user', 'GET', {
      address,
    });
  }

  /* Initiate an outbound verification for an sms device:
   *
   * 201 Success. A token has been generated for further verification.
   * 403 The address is specified properly but can not be verified.
   * 409 The currently authenticated user already has the device.
   * 422 The syntax of the address is invalid.
   */
  static outboundVerify(endpoint) {
    return apiRequest('/devices/sms/outbound_verification/tokens', 'POST', {
      endpoint,
    });
  }

  /* Create the device after having initiated a outbound verification
   * Pass the token returned by outboundVerify and the code
   * provided by the user (received by sms).
   *
   * 201  Success. The device has been added.
   * 403  The provided token and code are invalid.
   */
  static createOutboundDevice(token, code) {
    return apiRequest('/devices/sms/outbound_verification', 'POST', {
      token,
      code,
    });
  }

  /* Initiate the merge request for merging two email devices

   * 201 Success. A token has been generated for further verification.
   * 404 The request could not be found.
   * 403 The address is specified properly but can not be verified.
   * 409 The currently authenticated user already has the device.
   * 422 The syntax of the address is invalid.
   */
  static createEmailMergeRequest(endpoint) {
    return apiRequest('/devices/email/merge_confirmation', 'POST', {
      endpoint,
    });
  }

  /* Merge two two email accounts after having initiated a merge request
   * Pass the token returned by createEmailRequest and the token returned
   * by the User. Also pass the ID of the two users.
  */
  static emailMergeRequest(code, oldUserEmail) {
    const tz = jstz.determine();
    const timezone = tz.name();
    return apiRequest('/devices/email/merge_emails', 'POST', {
      code,
      email: oldUserEmail,
      timezone,
    });
  }
}



// WEBPACK FOOTER //
// ./src/entities/Device.js